import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * MultiBrandSelect
 * Popover with search + checkboxes. Selected count shown on the trigger.
 * `value` is an array of brand_ids (strings). Empty array = "All my brands".
 *
 * Props:
 *   brands       — [{ brand_id, brand_name }]
 *   value        — string[]  (empty = all)
 *   onChange     — (arr: string[]) => void
 *   testId       — optional data-testid
 *   labelAllText — text when nothing selected (default "All my brands")
 */
export default function MultiBrandSelect({ brands, value, onChange, testId = "brand-multi", labelAllText = "All my brands" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = value || [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return brands;
    return brands.filter((b) => (b.brand_name || "").toLowerCase().includes(s) || String(b.brand_id).includes(s));
  }, [brands, q]);

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };
  const selectAll = () => onChange(brands.map((b) => String(b.brand_id)));
  const clear = () => onChange([]);

  const label =
    selected.length === 0
      ? labelAllText
      : selected.length === 1
      ? brands.find((b) => String(b.brand_id) === selected[0])?.brand_name || `#${selected[0]}`
      : `${selected.length} brands`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="glass-input justify-between h-9 min-w-[220px] font-normal text-slate-900 hover:bg-white"
          data-testid={testId}
        >
          <span className="truncate flex items-center gap-2">
            {label}
            {selected.length > 0 && selected.length < brands.length && (
              <Badge variant="secondary" className="ml-1 h-4 text-[10px] px-1.5">{selected.length}</Badge>
            )}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="p-2 border-b border-slate-200/70 relative">
          <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search brands…"
            className="h-8 pl-7 text-xs"
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 text-[10px] uppercase text-slate-500 border-b border-slate-200/70">
          <button onClick={selectAll} className="hover:text-slate-900" data-testid={`${testId}-select-all`}>Select all</button>
          <button onClick={clear} className="hover:text-slate-900 flex items-center gap-0.5" data-testid={`${testId}-clear`}>
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-500 text-center">No brands match.</div>
          ) : filtered.map((b) => {
            const id = String(b.brand_id);
            const checked = selected.includes(id);
            return (
              <label
                key={id}
                className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50"
                data-testid={`${testId}-opt-${id}`}
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(id)} />
                <span className="truncate">{b.brand_name}</span>
                <span className="ml-auto text-[10px] text-slate-400">#{id}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
